/**
 * Transportation — Wave 12.ab · Phase 3
 * ─────────────────────────────────────────────────────────────────
 * Shared utilities + UI for the Transportation module.
 *
 *   • TRANSPORT_TYPES / STATUS / TIMING / VISIBILITY enums
 *   • toTransportBanner(t, { isSurprise, isAdvisorView })
 *       Returns { icon, eyebrow, body, sub, allowed } describing how
 *       a transport block should appear in the client suite — applies
 *       visibility, status-pending, and surprise-trip protection.
 *   • TransportEditor — form section used inside the Slot + Extra
 *       editors.  Driven by `value` / `onChange` so it stays stateless.
 */
import React from "react";
import { Car, Footprints, EyeOff, AlertCircle } from "lucide-react";

export const TRANSPORT_TYPES = [
  { key: "private_driver",   label: "Private Driver" },
  { key: "hotel_house_car",  label: "Hotel House Car" },
  { key: "transfer",         label: "Transfer Service" },
  { key: "shuttle",          label: "Shuttle" },
  { key: "taxi_uber",        label: "Taxi / Uber" },
  { key: "walking",          label: "Walking" },
  { key: "own",              label: "Own Arrangements" },
  { key: "other",            label: "Other" },
];

export const TRANSPORT_STATUS = [
  { key: "arranged", label: "Transportation Arranged" },
  { key: "own",      label: "Make Own Arrangements" },
  { key: "pending",  label: "Transportation Pending (advisor only)" },
];

export const TRANSPORT_TIMING = [
  { key: "before", label: "Before event" },
  { key: "after",  label: "After event" },
  { key: "both",   label: "Both" },
];

export const TRANSPORT_VISIBILITY = [
  { key: "full",    label: "Full details · pickup time + provider + destination" },
  { key: "limited", label: "Limited · pickup time only (no destination or event name)" },
  { key: "hidden",  label: "Hidden · nothing until reveal" },
];

const ARRANGED_TYPES = new Set(["private_driver", "hotel_house_car", "transfer", "shuttle", "taxi_uber", "other"]);

/**
 * Resolve how a transport block should render for the client suite.
 * Returns `{ allowed, icon, eyebrow, body, sub }` — when `allowed`
 * is false the caller should render nothing.
 *
 * The function bakes in all four protection rules:
 *   1. Pending status → advisor only, never client visible.
 *   2. Hidden visibility → never client visible.
 *   3. Limited visibility OR surprise event → only pickup readiness,
 *      no destination/event names.
 *   4. Walking / Own arrangements → friendly messages, optional guidance.
 */
export function resolveTransportDisplay(t, { isSurprise = false, isAdvisorView = false, itemName = "" } = {}) {
  if (!t || typeof t !== "object") return { allowed: false };
  const status = t.status || "arranged";
  const type = t.type || "other";
  const visibility = t.visibility || "full";

  // Pending — advisor only.
  if (status === "pending") {
    return isAdvisorView
      ? { allowed: true, icon: AlertCircle, eyebrow: "Transportation pending", body: "Not yet confirmed.", sub: t.notes || null, advisorOnly: true }
      : { allowed: false };
  }

  // Hidden visibility — silent client-side.
  if (!isAdvisorView && visibility === "hidden") return { allowed: false };

  // Own Arrangements
  if (status === "own" || type === "own") {
    return {
      allowed: true,
      icon: Footprints,
      eyebrow: "Make your own arrangements",
      body: t.client_guidance || "Please make your own way to the venue.",
      sub: t.pickup_time ? `By ${t.pickup_time}` : null,
    };
  }

  // Walking
  if (type === "walking") {
    return {
      allowed: true,
      icon: Footprints,
      eyebrow: "Walking recommended",
      body: t.client_guidance || (t.pickup_location ? `From ${t.pickup_location}` : ""),
      sub: t.pickup_time ? `Leave by ${t.pickup_time}` : null,
    };
  }

  // Anything Arranged (driver / house car / transfer / etc.)
  if (ARRANGED_TYPES.has(type)) {
    const limit = !isAdvisorView && (visibility === "limited" || isSurprise);
    if (limit) {
      // No destination, no event name. Pickup time + readiness only.
      return {
        allowed: true,
        icon: Car,
        eyebrow: "Transportation arranged",
        body: t.client_guidance || "Please be ready in the lobby.",
        sub: t.pickup_time ? `Pickup ${t.pickup_time}` : null,
      };
    }
    // Full visibility — advisor view or non-surprise non-limited.
    const detailBits = [];
    if (t.provider) detailBits.push(t.provider);
    if (t.pickup_location) detailBits.push(t.pickup_location);
    const subBits = [];
    if (t.pickup_time) subBits.push(t.pickup_time);
    if (isAdvisorView && t.confirmation_number) subBits.push(`Conf #${t.confirmation_number}`);
    return {
      allowed: true,
      icon: Car,
      eyebrow: "Transportation arranged",
      body: detailBits.join(" · ") || (itemName ? `Arrival at ${itemName}` : "Pickup arranged."),
      sub: subBits.join(" · ") || null,
    };
  }

  return { allowed: false };
}

// ─── Editor form ────────────────────────────────────────────────────
export function TransportEditor({ value, onChange, isSurprise = false, testIdPrefix = "transport" }) {
  const t = value || {};
  const set = (patch) => onChange({ ...t, ...patch });
  const status = t.status || "";
  const type = t.type || "";

  const inputClass = "w-full px-3 py-2 border border-[rgba(0,0,0,0.1)] rounded text-[13.5px] focus:outline-none focus:border-[hsl(var(--accent-gold))]";
  const labelClass = "block font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] mb-1";

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      <div>
        <label className={labelClass}>Status</label>
        <select
          value={status}
          onChange={(e) => set({ status: e.target.value })}
          className={inputClass}
          data-testid={`${testIdPrefix}-status`}
        >
          <option value="">— none —</option>
          {TRANSPORT_STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {status && status !== "pending" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Type</label>
              <select
                value={type}
                onChange={(e) => set({ type: e.target.value })}
                className={inputClass}
                data-testid={`${testIdPrefix}-type`}
              >
                <option value="">— pick one —</option>
                {TRANSPORT_TYPES.map((tt) => <option key={tt.key} value={tt.key}>{tt.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Timing</label>
              <select
                value={t.timing || ""}
                onChange={(e) => set({ timing: e.target.value })}
                className={inputClass}
                data-testid={`${testIdPrefix}-timing`}
              >
                <option value="">— pick one —</option>
                {TRANSPORT_TIMING.map((tg) => <option key={tg.key} value={tg.key}>{tg.label}</option>)}
              </select>
            </div>
          </div>

          {type !== "own" && type !== "walking" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Pickup time</label>
                <input
                  value={t.pickup_time || ""}
                  onChange={(e) => set({ pickup_time: e.target.value })}
                  placeholder="6:30 PM"
                  className={inputClass}
                  data-testid={`${testIdPrefix}-pickup-time`}
                />
              </div>
              <div>
                <label className={labelClass}>Pickup location</label>
                <input
                  value={t.pickup_location || ""}
                  onChange={(e) => set({ pickup_location: e.target.value })}
                  placeholder="Hotel lobby"
                  className={inputClass}
                  data-testid={`${testIdPrefix}-pickup-location`}
                />
              </div>
              <div>
                <label className={labelClass}>Provider</label>
                <input
                  value={t.provider || ""}
                  onChange={(e) => set({ provider: e.target.value })}
                  placeholder="Carey NYC"
                  className={inputClass}
                  data-testid={`${testIdPrefix}-provider`}
                />
              </div>
              <div>
                <label className={labelClass}>Confirmation #</label>
                <input
                  value={t.confirmation_number || ""}
                  onChange={(e) => set({ confirmation_number: e.target.value })}
                  placeholder="ABC-12345"
                  className={inputClass}
                  data-testid={`${testIdPrefix}-confirmation`}
                />
              </div>
            </div>
          )}

          <div>
            <label className={labelClass}>Client-facing guidance</label>
            <textarea
              rows={2}
              value={t.client_guidance || ""}
              onChange={(e) => set({ client_guidance: e.target.value })}
              placeholder={type === "own" ? "Uber/Lyft recommended. Estimated 12 min."
                : type === "walking" ? "Walk down Broadway. Estimated 10 min."
                : "Please be ready in the lobby."}
              className={`${inputClass} resize-none`}
              data-testid={`${testIdPrefix}-guidance`}
            />
            <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))] mt-1 leading-snug">
              This is the only line the client will read when visibility is set to Limited.
            </p>
          </div>

          <div>
            <label className={labelClass}>Internal notes</label>
            <textarea
              rows={2}
              value={t.notes || ""}
              onChange={(e) => set({ notes: e.target.value })}
              placeholder="Driver name, vehicle, internal contact, etc."
              className={`${inputClass} resize-none`}
              data-testid={`${testIdPrefix}-notes`}
            />
            <p className="font-accent italic text-[11px] text-[hsl(var(--foreground-muted))] mt-1 leading-snug">
              Never shown to the client.
            </p>
          </div>
        </>
      )}

      {status && status !== "pending" && (
        <div className={`rounded-md px-3 py-2.5 ${isSurprise ? "bg-[hsl(15,55%,55%)]/10 border border-[hsl(15,55%,55%)]/30" : "bg-[hsl(var(--background-paper))]/60"}`}>
          <label className={labelClass}>
            Client visibility
            {isSurprise && <span className="ml-2 font-accent italic text-[10px] normal-case tracking-normal text-[hsl(15,55%,40%)]">· Surprise event — destination is auto-protected.</span>}
          </label>
          <div className="space-y-1.5 mt-1">
            {TRANSPORT_VISIBILITY.map((v) => (
              <label key={v.key} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`${testIdPrefix}-visibility`}
                  checked={(t.visibility || "full") === v.key}
                  onChange={() => set({ visibility: v.key })}
                  className="mt-1 accent-[hsl(var(--accent-gold))]"
                  data-testid={`${testIdPrefix}-visibility-${v.key}`}
                />
                <span className="font-accent text-[12.5px] text-[hsl(var(--foreground-secondary))] leading-relaxed">
                  <span className="font-mono uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--foreground))]">{v.label.split(" · ")[0]}</span>
                  {v.label.includes(" · ") && (
                    <span className="block italic text-[11px] text-[hsl(var(--foreground-muted))]">{v.label.split(" · ").slice(1).join(" · ")}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {status === "pending" && (
        <p className="font-accent italic text-[12.5px] text-[hsl(15,55%,40%)] inline-flex items-start gap-1.5">
          <EyeOff className="size-3 mt-0.5" strokeWidth={1.5} />
          Pending transportation is never visible to the client.
        </p>
      )}
    </div>
  );
}
